import React, { useState, Fragment, useEffect, useRef, useMemo, useCallback } from 'react'
import cn from 'classnames'

import Icon from '../Icon2/Icon'
import Tooltip from '../Tooltip/Tooltip'
import './SideBarWindow.css'
import Dropdown from '../DropDown/Dropdown'

type subTabsArray = {
  id: string;
  tabLabel?: string;
  /** When true, shows `tabLabel` under the tab icon (truncated). Tooltip still uses `tabLabel`. */
  isTabLabelVisible?: boolean;
  tabIcon?: React.ReactNode;
  hasNotification?: Boolean;
  notifTabIcon?: React.ReactNode;
  data?: React.ReactNode;
  windowTitle?: string | React.ReactNode;
  width?: string;
  discussionPanelClassName?: string;
  disableSidebar?: boolean;
  enableTab?: boolean;
  enableInEntryCreatePage?: boolean;
  isIndependentlyActive?: boolean;
  onClick: () => void;
  tabTestId?: string;
  className?: string;
};

type IArray = {
  id: string
  tabLabel?: string
  /** When true, shows `tabLabel` under the tab icon (truncated). Tooltip still uses `tabLabel`. */
  isTabLabelVisible?: boolean
  tabIcon?: React.ReactNode
  hasNotification?: Boolean
  notifTabIcon?: React.ReactNode
  data?: React.ReactNode
  windowTitle?: string | React.ReactNode
  width?: string
  discussionPanelClassName?: string
  disableSidebar?: boolean
  enableTab?: boolean
  enableInEntryCreatePage?: boolean
  isIndependentlyActive?: boolean
  onClick: () => void
  tabTestId?: string
  className?: string
  multipleTabs?: boolean;
  subTabsInfo?: subTabsArray[];
  waitOnClick?: () => Promise<any>
}

interface SideBarWindowProps {
  /**
   * An array of tab info objects(id, tabLabel, tabIcon, data)
   */
  tabsInfo?: Array<IArray>
  windowData?: {
    tabLabel?: string
    /** When true, shows `tabLabel` under the tab icon (truncated). Tooltip still uses `tabLabel`. */
    isTabLabelVisible?: boolean
    tabIcon?: React.ReactNode
    data?: React.ReactNode
    windowTitle?: string | React.ReactNode
    tabTestId?: string
  }
  className?: string
  isInsideEntryCreatePage?: boolean
  onSelect?: (data?: any) => void
  onClose?: (data?: any) => void
  borderOpen?: boolean
  borderClose?: boolean
  isResizable?: boolean
  defaultSelectedIndex?: number
  defaultSelectedId?: string
  onKeyDownCallback?: Function
  onResize?: Function
  defaultSelectedSubTabId?: string
  defaultSelectedSubTabIndex?: number
  /**
   * A boolean indicating whether the sidebar content is visible.
   * - `true`: The sidebar content is displayed.
   * - `false`: The sidebar content is hidden.
   * This prop is controlled by the parent component, allowing the parent to manage the visibility of the sidebar.
   */
  isSidebarContentVisible?: boolean

}
export type SideBarResizeDomIds = {
  panelId: string
  resizeBarId: string
  bottomLayerId: string
}

interface WindowContentProps {
  data?: React.ReactNode
  windowTitle?: string | React.ReactNode
  width?: string
  discussionPanelClassName?: string
  isResizable?: boolean
  onResize?: Function,
  activeTabId: number
  isSubTab?: boolean
  /** Unique DOM ids when multiple resizable sidebars exist (e.g. entry + entry-in-modal). */
  resizeDomIds?: SideBarResizeDomIds
}

const getSidebarTabTooltipContent = (tabLabel?: string): string => {
  if (typeof tabLabel === 'string' && tabLabel.trim().length > 0) {
    return tabLabel
  }
  return ''
}

export const getSelectedTabIndex = (tabsInfo: Array<any>, tabId?: string, tabIndex?: number) => {
  if (tabId) {
    const selectedTabIndex = tabsInfo?.findIndex(eachTab => eachTab.id === tabId);
    return selectedTabIndex >= 0 ? selectedTabIndex : null;
  }
  if (Number.isInteger(tabIndex) && tabIndex < tabsInfo?.length) {
    return tabIndex;
  }
  return null;
}

const DEFAULT_RESIZE_DOM_IDS: SideBarResizeDomIds = {
  panelId: 'resizable__panel',
  resizeBarId: 'sidebar-resize-bar',
  bottomLayerId: 'SidebarWindow-bottom-layer',
}

export const GetWindowContent = (props: WindowContentProps) => {
  const { data, windowTitle, width = '400px', discussionPanelClassName, isResizable, onResize, activeTabId, resizeDomIds } = props
  const domIds = resizeDomIds ?? DEFAULT_RESIZE_DOM_IDS
  const { panelId, resizeBarId, bottomLayerId } = domIds
  const oldResizeWidthRef = useRef(parseInt(width, 10));
  const onResizeRef = useRef(onResize)
  onResizeRef.current = onResize

  useEffect(() => {
    oldResizeWidthRef.current = parseInt(width, 10)
  }, [width])

  const rsMouseDownHandler = useCallback((e) => {
    e.preventDefault();
    const widthInNumber = parseInt(width, 10)
    const x = e.clientX;
    const panel = document.getElementById(panelId)
    if (!panel) {
      return
    }
    const renderedWidth = Math.round(panel.getBoundingClientRect().width);
    const initialWidth = Math.max(oldResizeWidthRef.current, renderedWidth);
    let newWidth = initialWidth;

    const bottomLayer = document.getElementById(bottomLayerId)
    if (bottomLayer) bottomLayer.style.display = "block";

    const mouseUpHandler = (e) => {
      e.preventDefault();
      const bottomLayerEl = document.getElementById(bottomLayerId)
      if (bottomLayerEl) bottomLayerEl.style.display = "none";
      document.removeEventListener('mouseup', mouseUpHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
      const currentWidth = newWidth > 640 ? 640 : (newWidth < widthInNumber ? widthInNumber : newWidth);
      const resizeCb = onResizeRef.current
      resizeCb && resizeCb({ width: initialWidth, currentWidth })
      oldResizeWidthRef.current = currentWidth;
    };

    const mouseMoveHandler = (e) => {
      e.preventDefault();
      const dx = x - e.clientX; // Resize from left to right
      const bottomLayerEl = document.getElementById(bottomLayerId)
      if (bottomLayerEl) bottomLayerEl.style.display = "block";
      newWidth = initialWidth + dx;

      if (newWidth <= 640 && newWidth >= widthInNumber) panel.style.width = newWidth + 'px';
      else if (newWidth > 640) panel.style.width = '640px';
      else if (newWidth < widthInNumber) panel.style.width = widthInNumber + 'px';

      if (newWidth <= 400) {
        panel.classList.add('resizable__panel--min')
      } else {
        panel.classList.remove('resizable__panel--min')
      }
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  }, [width, panelId, bottomLayerId]);

  useEffect(() => {
    if (!isResizable) {
      return
    }
    const panel = document.getElementById(panelId)
    if (!panel) {
      return
    }
    panel.style.width = width
    document.getElementById(resizeBarId)?.addEventListener('mousedown', rsMouseDownHandler);

    return () => {
      const resizeBar = document.getElementById(resizeBarId);
      if (resizeBar) {
        resizeBar.removeEventListener('mousedown', rsMouseDownHandler);
      }
    };
  }, [isResizable, width, panelId, resizeBarId, bottomLayerId, rsMouseDownHandler])

  if (data) {
    let style: any = { width: isResizable ? 'calc(100% - 72px)' : width }

    return (
      <div className={['SidebarWindow__content', discussionPanelClassName].join(' ')} style={style}>
        <div className="SidebarWindow__content__title">{windowTitle}</div>
        <div className="SidebarWindow__content__body">{data}</div>
      </div>
    )
  }

  return <p>No data defined for the tab</p>
}

const bottomLayerStyle: any = {
  position: "absolute",
  width: window.innerWidth,
  height: "100%",
  left: `-${window.innerWidth - 72}px`,
  display: "none",
  zIndex: 1,
  cursor: "col-resize",
};

const SideBarWindow = (props: SideBarWindowProps) => {
  const activeTabIndex = getSelectedTabIndex(props?.tabsInfo, props?.defaultSelectedId, props?.defaultSelectedIndex)
  const [isComponentVisible, setIsComponentVisible] = useState(Number.isInteger(activeTabIndex) && activeTabIndex < props?.tabsInfo?.length)
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    Number.isInteger(activeTabIndex) && activeTabIndex < props?.tabsInfo?.length ? activeTabIndex : null
  )
  const activeSubTabIndex = getSelectedTabIndex(
    props.tabsInfo?.[selectedTabIndex]?.subTabsInfo ?? [],
    props?.defaultSelectedSubTabId,
    props?.defaultSelectedSubTabIndex
  )
  const [selectedSubTabIndex, setSelectedSubTabIndex] = useState(
    Number.isInteger(activeSubTabIndex) &&
      activeSubTabIndex < (props.tabsInfo?.[selectedTabIndex]?.subTabsInfo?.length ?? 0)
      ? activeSubTabIndex
      : null
  )

  const { tabsInfo = [], windowData, isInsideEntryCreatePage = false, className, onSelect, onClose, isResizable, onKeyDownCallback = () => { } } = props
  const [refresh, setRefresh] = useState(false) // this is an arbitary value just to reload component

  const resizeDomIds = useMemo((): SideBarResizeDomIds => {
    const uid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 11)}`
    return {
      panelId: `resizable__panel-${uid}`,
      resizeBarId: `sidebar-resize-bar-${uid}`,
      bottomLayerId: `SidebarWindow-bottom-layer-${uid}`,
    }
  }, [])

  const initializeTabs = () => {
    const activeTabIndex = getSelectedTabIndex(
      tabsInfo,
      props?.defaultSelectedId,
      props?.defaultSelectedIndex
    );
    const activeSubTabIndex = getSelectedTabIndex(
      tabsInfo[activeTabIndex]?.subTabsInfo,
      props?.defaultSelectedSubTabId,
      props?.defaultSelectedSubTabIndex
    );

    const nextSelectedTabIndex = Number.isInteger(activeTabIndex) && activeTabIndex < tabsInfo.length ? activeTabIndex : null;
    const subTabsLength = Number.isInteger(activeTabIndex)
      ? tabsInfo[activeTabIndex]?.subTabsInfo?.length ?? 0
      : 0;
    const nextSelectedSubTabIndex =
      Number.isInteger(activeSubTabIndex) && activeSubTabIndex < subTabsLength ? activeSubTabIndex : null;

    setIsComponentVisible(Number.isInteger(activeTabIndex) && activeTabIndex < tabsInfo.length);
    setSelectedTabIndex(nextSelectedTabIndex);
    setSelectedSubTabIndex(nextSelectedSubTabIndex);
  };

  React.useEffect(() => {
    initializeTabs();
  }, [tabsInfo?.[activeTabIndex ?? -1]?.subTabsInfo?.length ?? 0, tabsInfo.length])


  const selectTab = (index: number) => {
    if (!tabsInfo[index].hasOwnProperty('disableSidebar') && !tabsInfo[index].disableSidebar) {
      if (tabsInfo[index].hasOwnProperty('enableTab') && tabsInfo[index].enableTab === true) {
        if (tabsInfo[index].hasOwnProperty('onClick')) {
          tabsInfo[index].onClick()
          tabsInfo[index].isIndependentlyActive = !tabsInfo[index].isIndependentlyActive
          setRefresh((prevValue) => !prevValue)
        }
      } else if (!isInsideEntryCreatePage) {
        if (tabsInfo[index].hasOwnProperty('onClick')) {
          tabsInfo[index].onClick()
        }
        if (index === selectedTabIndex && isComponentVisible) {
          setIsComponentVisible(false)
          onClose && onClose()
          return
        }

        if (tabsInfo[index].hasOwnProperty('waitOnClick')) {
          tabsInfo[index].waitOnClick()
            .then(response => {
              if (response === true) {
                setSelectedTabIndex(index);
                setIsComponentVisible(true);
                if (onSelect) {
                  onSelect();
                }
              }
            })
            .catch(error => {
              console.error('Error during waitOnClick:', error);
            });
          return;
        }
        setIsComponentVisible(true)
        setSelectedTabIndex(index)
        if (onSelect) {
          onSelect()
        }
      }
    }
  }

  const selectSubTab = (parentTabIndex: number, subTabIndex: number) => {
    if (parentTabIndex >= 0 && parentTabIndex < tabsInfo.length) {
      const parentTab = tabsInfo[parentTabIndex];
      if (parentTab && parentTab.subTabsInfo && subTabIndex >= 0 && subTabIndex < parentTab?.subTabsInfo?.length) {
        const subTab = parentTab?.subTabsInfo[subTabIndex];
        if (!subTab.hasOwnProperty('disableSidebar') && !subTab.disableSidebar) {
          if (subTab.hasOwnProperty('onClick')) {
            subTab.onClick();
          }
          if (subTabIndex === selectedSubTabIndex && isComponentVisible) {
            setIsComponentVisible(false);
            onClose && onClose();
            return;
          }
          setIsComponentVisible(true);
          setSelectedTabIndex(parentTabIndex);
          setSelectedSubTabIndex(subTabIndex);
          if (onSelect) {
            onSelect();
          }
        }
      }
    }
  };

  const onCloseWindow = () => {
    if (!isInsideEntryCreatePage) {
      setIsComponentVisible(false)
      setSelectedTabIndex(null)
      if (onClose) onClose()
    }
  }

  const onKeyPressTabs = (e: any, index: number) => {
    if (e.key === "Enter" || e.key === " ") {
      selectTab(index)
    }
    props.onKeyDownCallback && props.onKeyDownCallback(e, (index === tabsInfo.length - 1))
  }

  useEffect(() => {
    if (typeof props.isSidebarContentVisible == "boolean") {
      if (props.isSidebarContentVisible) {
        // Set the sidebar as visible if `selectedTabIndex` exists and is valid.
        setIsComponentVisible(Number.isInteger(selectedTabIndex) && selectedTabIndex < tabsInfo.length);
      } else {
        // Close the sidebar window if `isSidebarContentVisible` is false.
        onCloseWindow();
      }
    }
  }, [props.isSidebarContentVisible]);

  return (
    <div
      data-test-id={props.windowData?.tabTestId ? `${props.windowData?.tabTestId}-window` : 'cs-sidebar-window'}
      className={`SidebarWindow flex ${isComponentVisible ? 'SidebarWindow--active' : ''} ${className ? className : ''}`} >
      {isResizable && <div id={resizeDomIds.bottomLayerId} style={bottomLayerStyle} />}
      {props.hasOwnProperty('windowData') ? (
        <>
          {isComponentVisible && (
            <div>
              <div onClick={() => onCloseWindow()} className="RightSideHide SidebarWindow__hide-icon-container">
                <Icon icon="CaretRight" size="tiny" version='v2' />
              </div>
              <GetWindowContent data={windowData.data} windowTitle={windowData.windowTitle} onResize={props.onResize} activeTabId={activeTabIndex} />
            </div>
          )}
        </>
      ) : (
        <>
          {isComponentVisible && (
            <div id={isResizable ? resizeDomIds.panelId : ''} className={isResizable ? 'resizable__panel' : undefined}>
              <div className="resizable__panel__stroke" id={isResizable ? resizeDomIds.resizeBarId : undefined}></div>
              <div onClick={() => onCloseWindow()} className="RightSideHide SidebarWindow__hide-icon-container"><Icon icon="CaretRight" size="tiny" version='v2' /></div>

              {/* Conditional rendering based on multipleTabs */}
              {tabsInfo[selectedTabIndex]?.multipleTabs ? (
                // Render content based on selected subtab
                <GetWindowContent
                  data={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].data}
                  windowTitle={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].windowTitle}
                  width={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].width}
                  discussionPanelClassName={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].discussionPanelClassName}
                  isResizable={isResizable}
                  onResize={props.onResize}
                  activeTabId={activeTabIndex}
                  resizeDomIds={isResizable ? resizeDomIds : undefined}
                />
              ) : (
                // Render content for single tab
                <GetWindowContent
                  data={tabsInfo[selectedTabIndex].data}
                  windowTitle={tabsInfo[selectedTabIndex].windowTitle}
                  width={tabsInfo[selectedTabIndex].width}
                  discussionPanelClassName={tabsInfo[selectedTabIndex].discussionPanelClassName}
                  isResizable={isResizable}
                  activeTabId={activeTabIndex}
                  onResize={props.onResize}
                  resizeDomIds={isResizable ? resizeDomIds : undefined}
                />
              )}
            </div>
          )}
        </>
      )}

      <div
        className={`SidebarWindow__tabs-container
      ${!isComponentVisible && props.borderClose ? 'SidebarWindow__tabs-container--border' : ''}
      ${isComponentVisible && props.borderOpen ? 'SidebarWindow__tabs-container--border' : ''}`}>
        {props.hasOwnProperty('windowData') ? (
          <>
            {windowData && (
              <Tooltip
                testId={windowData.tabTestId}
                content={getSidebarTabTooltipContent(windowData.tabLabel)}
                position="left"
                showArrow={false}
                interactive={false}
                disabled={getSidebarTabTooltipContent(windowData.tabLabel) === ''}
                popperOptions={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -3]
                      }
                    },
                  ]
                }}
              >
                <div tabIndex={0} onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setIsComponentVisible(!isComponentVisible)
                  }
                }} className="SidebarWindowSingle__tab-item">
                  <div
                    className={cn('SidebarWindow__tab-item', {
                      'SidebarWindow__tab-item--with-tab-label-visible': Boolean(
                        windowData.isTabLabelVisible && getSidebarTabTooltipContent(windowData.tabLabel)
                      ),
                    })}
                    onClick={() => setIsComponentVisible(!isComponentVisible)}
                  >
                    {windowData.tabIcon}
                    {windowData.isTabLabelVisible && getSidebarTabTooltipContent(windowData.tabLabel) ? (
                      <span
                        className="SidebarWindow__rail-tab-label"
                        data-test-id={
                          windowData.tabTestId
                            ? `${windowData.tabTestId}-tab-label-visible`
                            : 'cs-sidebar-window-tab-label-visible'
                        }
                      >
                        {getSidebarTabTooltipContent(windowData.tabLabel)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            {tabsInfo &&
              tabsInfo.map((tab, index) => {
                if (tab.multipleTabs) {
                  const maxVisibleSubtabs = tabsInfo[index].subTabsInfo.length > 3 ? 2 : 3; // Maximum number of subtabs to render directly
                  // Render subtabs
                  const visibleSubtabs = tabsInfo[index].subTabsInfo.slice(0, maxVisibleSubtabs);
                  const hiddenSubtabs = tabsInfo[index].subTabsInfo.slice(maxVisibleSubtabs);
                  const listForDropdown = hiddenSubtabs.map(subTab => ({
                    ...subTab,
                    label: (
                      <div className='flex flex-v-center col-gap-15'>
                        {subTab.tabIcon && subTab.tabIcon}
                        {subTab.tabLabel}
                      </div>
                    )
                  }));

                  const handleSubTabChange = (e, index) => {

                    const subTabsInfo = tabsInfo[index]?.subTabsInfo;
                    const subIndex = subTabsInfo.findIndex(subTab => subTab.id === e.id);
                    return subIndex;
                  };


                  return (
                    <div className='multiple-subtabs flex-column'>
                      {/* Render visible subtabs */}
                      {visibleSubtabs.map((subTab, subIndex) => {
                        const subTabClassName = cn('SidebarWindow__subtab-item', {
                          'SidebarWindow__subtab-item--selected': selectedTabIndex === index && selectedSubTabIndex === subIndex,
                          'SidebarWindow__subtab-item--disabled':
                            (!subTab.enableInEntryCreatePage && isInsideEntryCreatePage) ||
                            (subTab.hasOwnProperty('disableSidebar') && subTab.disableSidebar),
                          'SidebarWindow__subtab-item--independently-active': subTab.isIndependentlyActive,
                          'SidebarWindow__subtab-item--with-tab-label-visible': Boolean(
                            subTab.isTabLabelVisible && getSidebarTabTooltipContent(subTab.tabLabel)
                          ),
                        });
                        const subTabRailLabel =
                          subTab.isTabLabelVisible && getSidebarTabTooltipContent(subTab.tabLabel) ? (
                            <span
                              className="SidebarWindow__rail-tab-label"
                              data-test-id={
                                subTab.tabTestId
                                  ? `${subTab.tabTestId}-tab-label-visible`
                                  : 'cs-sidebar-subtab-tab-label-visible'
                              }
                            >
                              {getSidebarTabTooltipContent(subTab.tabLabel)}
                            </span>
                          ) : null;

                        return (
                          <Tooltip
                            key={subIndex}
                            testId={subTab.tabTestId}
                            content={getSidebarTabTooltipContent(subTab.tabLabel)}
                            position="left"
                            showArrow={false}
                            interactive={false}
                            disabled={getSidebarTabTooltipContent(subTab.tabLabel) === ''}
                            popperOptions={{
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, -10],
                                  },
                                },
                              ],
                            }}
                          >
                            {tab.hasNotification ? (
                              selectedTabIndex === index && isComponentVisible ? (
                                <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={subTabClassName} onClick={() => selectSubTab(index, subIndex)}>
                                  <div className='tab-iconWrapper'>
                                    {tab.tabIcon}
                                  </div>
                                  {subTabRailLabel}
                                </div>
                              ) : (
                                <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={subTabClassName} onClick={() => selectSubTab(index, subIndex)}>
                                  <div className='tab-iconWrapper'>{tab.notifTabIcon}</div>
                                  {subTabRailLabel}
                                </div>
                              )
                            ) : (
                              <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={subTabClassName} onClick={() => selectSubTab(index, subIndex)}>
                                <div className='tab-iconWrapper'>{subTab.tabIcon}</div>
                                {subTabRailLabel}
                              </div>
                            )}
                          </Tooltip>
                        );

                      })}

                      {/* Render dropdown for hidden subtabs */}
                      {hiddenSubtabs.length > 0 && (
                        <Tooltip
                          key={index}
                          testId={'subtabs_dropdown'}
                          content={'More Apps'}
                          position="left"
                          showArrow={false}
                          interactive={false}
                          popperOptions={{
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, -10],
                                },
                              },
                            ],
                          }}
                        >
                          <div className="subtabs_dropdown ">
                            <Dropdown
                              version='v2'
                              list={listForDropdown}
                              type="click"
                              ellipseAlignment="left"
                              isDisabled={false}
                              onChange={(e) => {
                                selectSubTab(index, handleSubTabChange(e, index));
                              }}
                              className='subtabsdropdown subtab_ellipses'
                            >
                              <Icon icon="AppDrawer" version='v2' size="small" />
                            </Dropdown>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  );

                }

                else {
                  // Render single tab
                  const tabClassName = cn('SidebarWindow__tab-item', {
                    'SidebarWindow__tab-item--selected': selectedTabIndex === index,
                    'SidebarWindow__tab-item--disabled':
                      (!tab.enableInEntryCreatePage && isInsideEntryCreatePage) ||
                      (tabsInfo[index].hasOwnProperty('disableSidebar') && tabsInfo[index].disableSidebar),
                    'SidebarWindow__tab-item--independently-active': tab.isIndependentlyActive,
                    'SidebarWindow__tab-item--with-tab-label-visible': Boolean(
                      tab.isTabLabelVisible && getSidebarTabTooltipContent(tab.tabLabel)
                    ),
                    '': refresh && false
                  }, tab.className);
                  const tabRailLabel =
                    tab.isTabLabelVisible && getSidebarTabTooltipContent(tab.tabLabel) ? (
                      <span
                        className="SidebarWindow__rail-tab-label"
                        data-test-id={
                          tab.tabTestId ? `${tab.tabTestId}-tab-label-visible` : 'cs-sidebar-tab-tab-label-visible'
                        }
                      >
                        {getSidebarTabTooltipContent(tab.tabLabel)}
                      </span>
                    ) : null;

                  return (
                    <Tooltip
                      key={index}
                      testId={tab.tabTestId}
                      content={getSidebarTabTooltipContent(tab.tabLabel)}
                      position="left"
                      showArrow={false}
                      interactive={false}
                      disabled={getSidebarTabTooltipContent(tab.tabLabel) === ''}
                      popperOptions={{
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -10],
                            },
                          },
                        ],
                      }}
                    >
                      {tab.hasNotification ? (
                        selectedTabIndex === index && isComponentVisible ? (
                          <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={tabClassName} onClick={() => selectTab(index)}>
                            <div className='tab-iconWrapper'>
                              {tab.tabIcon}
                            </div>
                            {tabRailLabel}
                          </div>
                        ) : (
                          <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={tabClassName} onClick={() => selectTab(index)}>
                            <div className='tab-iconWrapper'>{tab.notifTabIcon}</div>
                            {tabRailLabel}
                          </div>
                        )
                      ) : (
                        <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={tabClassName} onClick={() => selectTab(index)}>
                          <div className='tab-iconWrapper'>{tab.tabIcon}</div>
                          {tabRailLabel}
                        </div>
                      )}
                    </Tooltip>
                  );
                }
              })
            }


          </>
        )}
      </div>
    </div>
  )
}

export default SideBarWindow